// src/pages/msp-inventory/pages/reports/SalesTaxReportPage.jsx
import React, { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { rangeToParams } from "../../components/reports/DateRangePicker";
import MonthQuarterRangePicker from "../../components/reports/MonthQuarterRangePicker";
import KpiCard from "../../components/reports/KpiCard";
import { SortableTH, Pagination } from "../../components/reports/TableControls";
import { getSalesTaxReport } from "../../services/catalogService";
import { usePersistedState } from "../../hooks/usePersistedState";
import { useSortAndPaginate } from "../../hooks/useSortAndPaginate";
import RefreshableSection from "../../components/reports/RefreshableSection";
import { buildCacheKey, getCachedReport, setCachedReport, invalidateCachedReport } from "../../utils/reportCache";
import RefreshReportBar from "../../components/reports/RefreshReportBar";
import ExportCsvButton from "../../components/reports/ExportCsvButton";
import { downloadCsv } from "../../utils/exportCsv";
import LoadingGame from "../../components/reports/LoadingGame";

const SalesTaxReportPage = () => {
    const [range, setRange] = usePersistedState("reports:sales-tax:range", (() => {
        const now = new Date();
        const y = now.getFullYear(), m = now.getMonth();
        const start = `${y}-${String(m + 1).padStart(2, "0")}-01`;
        const end = new Date(y, m + 1, 0).toISOString().slice(0, 10);
        return { key: "custom", mode: "month", year: y, month: m, start, end };
    })());
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [savedAt, setSavedAt] = useState(null);

    const cacheKey = buildCacheKey("sales-tax", rangeToParams(range));

    const loadReport = async (forceRefresh = false) => {
        const isInitialLoad = !data;

        if (!forceRefresh) {
            const cached = await getCachedReport(cacheKey);
            if (cached) {
                setData(cached.data);
                setSavedAt(cached.savedAt);
                setLoading(false);
                return;
            }
        }

        if (isInitialLoad) setLoading(true);
        else setRefreshing(true);
        try {
            const d = await getSalesTaxReport(rangeToParams(range));
            setData(d);
            setSavedAt(Date.now());
            setCachedReport(cacheKey, d);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        loadReport();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [range]);

    const handleForceRefresh = async () => {
        await invalidateCachedReport(cacheKey);
        await loadReport(true);
    };

    const { sorted, pageItems, page, setPage, totalPages, sortConfig, requestSort, pageSize } =
        useSortAndPaginate(data?.rows || [], { key: "tax_collected", dir: "desc" }, "reports:sales-tax:table");

    if (loading && !data) return <LoadingGame label="Loading sales tax..." />;
    if (!data) return null;

    const { totals } = data;
    const topStates = sorted.slice(0, 10);

    return (
        <div className="max-w-6xl mx-auto py-8 px-6">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800">Sales Tax</h1>
                    <p className="text-gray-500 text-sm">Tax collected, by shipping state</p>
                </div>
                <div className="flex items-center gap-2">
                    <RefreshReportBar savedAt={savedAt} refreshing={refreshing} onRefresh={handleForceRefresh} onAutoRefresh={handleForceRefresh} />
                    <ExportCsvButton onExport={() => downloadCsv(
                        `sales_tax_${new Date().toISOString().slice(0, 10)}.csv`,
                        ["State", "Revenue", "Tax Collected", "Orders"],
                        sorted.map((s) => [s.state, s.revenue.toFixed(2), s.tax_collected.toFixed(2), s.orders]),
                        [["Tax Collected", totals.tax_collected.toFixed(2)], ["Total Revenue", totals.revenue.toFixed(2)], ["Orders", totals.orders]]
                    )} />
                    <MonthQuarterRangePicker range={range} setRange={setRange} />
                </div>
            </div>

            <RefreshableSection refreshing={refreshing} className="mb-6">
                <div className="grid grid-cols-3 gap-4">
                    <KpiCard label="Tax Collected" value={`$${totals.tax_collected.toFixed(2)}`} color="teal" />
                    <KpiCard label="Total Revenue" value={`$${totals.revenue.toFixed(2)}`} color="blue" />
                    <KpiCard label="Orders" value={totals.orders} color="purple" />
                </div>
            </RefreshableSection>

            <RefreshableSection refreshing={refreshing} className="mb-6">
                <div className="bg-white rounded-2xl shadow-lg p-5">
                    <h3 className="font-bold text-gray-800 text-sm mb-4">Top 10 States by Tax Collected</h3>
                    <ResponsiveContainer width="100%" height={280}>
                        <BarChart data={topStates}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                            <XAxis dataKey="state" tick={{ fontSize: 10 }} />
                            <YAxis tick={{ fontSize: 10 }} />
                            <Tooltip formatter={(v) => `$${Number(v).toFixed(2)}`} />
                            <Bar dataKey="tax_collected" fill="#9333ea" name="Tax Collected" radius={[3, 3, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </RefreshableSection>

            <RefreshableSection refreshing={refreshing}>
                <div className="bg-white rounded-2xl shadow-lg p-5 overflow-x-auto max-h-[70vh] overflow-y-auto">
                    <h3 className="font-bold text-gray-800 text-sm mb-3">By State</h3>
                    <table className="w-full text-xs">
                        <thead className="sticky top-0 z-10 bg-white">
                            <tr className="text-left text-gray-400 border-b border-gray-100">
                                <SortableTH label="State" sortKey="state" sortConfig={sortConfig} onSort={requestSort} />
                                <SortableTH label="Revenue" sortKey="revenue" sortConfig={sortConfig} onSort={requestSort} />
                                <SortableTH label="Tax Collected" sortKey="tax_collected" sortConfig={sortConfig} onSort={requestSort} />
                                <SortableTH label="Orders" sortKey="orders" sortConfig={sortConfig} onSort={requestSort} />
                            </tr>
                        </thead>
                        <tbody>
                            {pageItems.map((s) => (
                                <tr key={s.state} className="border-b border-gray-50">
                                    <td className="py-2 font-semibold">{s.state}</td>
                                    <td className="py-2">${s.revenue.toFixed(2)}</td>
                                    <td className="py-2 font-bold">${s.tax_collected.toFixed(2)}</td>
                                    <td className="py-2">{s.orders}</td>
                                </tr>
                            ))}
                            {pageItems.length === 0 && <tr><td colSpan={4} className="py-8 text-center text-gray-400">No orders in this range</td></tr>}
                        </tbody>
                    </table>
                    <Pagination page={page} totalPages={totalPages} setPage={setPage} totalItems={sorted.length} pageSize={pageSize} />
                </div>
            </RefreshableSection>
        </div>
    );
};

export default SalesTaxReportPage;